import React from 'react'

import { mtc } from '@/styles'

import { NextStepsActions } from './NextStepsActions'
import { useNextStepsActions } from './useNextStepsActions'
import { useReachedEndCard } from './useReachedEndCard'
import { useReportEndCardSlot } from './useReportEndCardSlot'

interface ReportStickyCtaProps {
  /** Whether this practice qualifies for a consult; decided by the API at intake submit. */
  isPql: boolean
}

/**
 * The report's closing actions, pinned to the bottom of the viewport on phones.
 *
 * The mobile report is a long scroll and its closing card sits at the very end, so the way
 * to act on the findings is off screen for most of the read. This puts it back in reach
 * without moving the card — the card is still where the report lands, and this is a
 * shortcut to it.
 *
 * Below `sm` only. Above it the card is pinned in the rail already (`STICKY_CTA_CLS`), so
 * a bar as well would be the same offer twice.
 *
 * Renders nothing for a decided rejection. `ReportView` already only mounts this alongside
 * the next-steps card, but the check is made here too, from the same hook `ReportEndCard`
 * uses: a bar that relies on its caller's guard would silently offer a rejected practice a
 * consult if it were ever mounted anywhere else.
 *
 * Deliberately not the DS `StickyBottomNav` (which the intake uses via
 * `OnboardingStickyNav`), despite the shape being the same. That primitive renders its
 * actions as `Button`s driven by `onBack`/`onContinue` handlers, and the consult CTA has
 * to be a real `<a href>` — it opens in a new tab, carries the replay-block class and
 * `data-dd-privacy`, and is tracked on click precisely because replay cannot see it. None
 * of that survives an `onClick`. The bar's own chrome is the only thing hand-rolled here;
 * the buttons inside it are the DS ones, via `NextStepsActions`.
 */
export const ReportStickyCta: React.FC<ReportStickyCtaProps> = ({ isPql }) => {
  const { verdict } = useReportEndCardSlot()
  const { taxTreatment, consultUrl, verdictName } = useNextStepsActions(isPql)
  const hidden = useReachedEndCard()

  if (verdict) return null

  return (
    <div
      // The report renders these same two actions in its closing card, so the bar needs a
      // handle of its own to be told apart from them.
      data-testid="report-sticky-cta"
      // Hidden from assistive tech as well as from sight while it is down, so the same two
      // actions are not announced twice once the card itself is on screen.
      aria-hidden={hidden}
      className={mtc(
        // `empty:hidden` takes the bar's border and background away in the one case where
        // `NextStepsActions` renders nothing — a non-PQL practice with no offers — rather
        // than leaving a bare strip pinned across the bottom of the screen.
        'fixed inset-x-0 bottom-0 z-10 border-t border-neutral-200 bg-neutral-0 px-4 py-4 transition-[opacity,visibility] empty:hidden sm:hidden',
        // `invisible` as well as `opacity-0`: opacity alone leaves the buttons in the tab
        // order, and focusable controls inside an `aria-hidden` subtree are exactly what a
        // screen reader cannot account for. `visibility` transitions too, so it still
        // fades rather than cutting.
        hidden && 'pointer-events-none invisible opacity-0'
      )}
    >
      {/* A row, not the card's stack: two full-width buttons stacked would take a third of
          a phone's viewport for chrome that is only meant to stay out of the way. */}
      <NextStepsActions
        taxTreatment={taxTreatment}
        consultUrl={consultUrl}
        verdict={verdictName}
        isPql={isPql}
        className="flex-row [&>*]:flex-1"
      />
    </div>
  )
}
